import { createFileRoute } from '@tanstack/react-router';
import { PageContainer } from '@/components/shared/PageContainer';
import { PageSkeleton } from '@/components/shared/PageSkeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { CompanyMergeDetailView } from '../../-components/CompanyMergeDetailView';
import {
  companyMergeDetailQueryOptions,
  useCompanyMergeDetail,
} from '../../-hooks/useCompanyMergeDetail';
import { useResumeCompanyMerge } from '../../-hooks/useResumeCompanyMerge';

export const Route = createFileRoute(
  '/management/company-merges/(pages)/$mergeId/',
)({
  // A failed prefetch must not blank the page — the component's own query
  // re-runs and surfaces the error inline.
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(companyMergeDetailQueryOptions(params.mergeId))
      .catch(() => undefined),
  component: CompanyMergeDetailPage,
});

function CompanyMergeDetailPage() {
  const { mergeId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const { merge, isLoading, isError } = useCompanyMergeDetail(mergeId);
  const { resume, resumingId } = useResumeCompanyMerge();

  useBreadcrumbLabel(
    mergeId,
    merge &&
      t('mergesPage.detail.breadcrumb', {
        source: merge.sourceCompanyName ?? merge.sourceCompanyId,
        target: merge.targetCompanyName ?? merge.targetCompanyId,
      }),
  );

  if (isLoading) return <PageSkeleton />;

  return (
    <PageContainer>
      {isError || !merge ? (
        <Alert variant='destructive' className='py-2.5'>
          <AlertDescription>
            {t('mergesPage.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      ) : (
        <CompanyMergeDetailView
          merge={merge}
          onResume={() => void resume(merge.id)}
          isResuming={resumingId === merge.id}
        />
      )}
    </PageContainer>
  );
}
